/**
 * Moms. Admin ger varje produktkategori en momssats, och databasen sparar
 * satsen på orderraden när ordern läggs (validate_and_calculate_order).
 * Reglerna här är desamma som i databasen och används för visning och för
 * fakturans uppställning. Alla priser är inklusive moms.
 *
 * - Frakt följer varornas sats (8 kap. 3 § ML). Har ordern flera satser
 *   fördelas frakten efter varornas värde exkl. moms, alltså
 *   beskattningsunderlaget (Skatteverket, skrivelse 2002-09-13). Den största
 *   satsgruppen får öresavrundningen så att delarna går jämnt upp.
 * - Momsen räknas per sats på summan inklusive moms och avrundas till hela
 *   ören: moms = brutto × sats / (100 + sats).
 */

/** Sveriges momssatser. */
export const VAT_RATES = [25, 12, 6, 0] as const;
export type VatRate = typeof VAT_RATES[number];

/**
 * Kort beskrivning i admin. Livsmedel (t.ex. kaffe i paket) har 6 %
 * 2026-04-01 till 2027-12-31 och sedan 12 % igen: lägg en planerad ändring.
 */
export const VAT_RATE_LABELS: Record<VatRate, string> = {
    25: 'Standard',
    12: 'Restaurang m.m.',
    6: 'Livsmedel, böcker',
    0: 'Momsfritt',
};

/** Satsen som gäller innan en vara har en kategori (som fakturorna räknat hittills). */
export const LEGACY_VAT_RATE: VatRate = 12;

export const isVatRate = (n: unknown): n is VatRate => VAT_RATES.includes(Number(n) as VatRate);

export interface ProductCategory {
    id: string;
    name: string;
    vatRate: VatRate;
    /** Planerad ändring: ny sats från ett datum (svensk tid). */
    nextVatRate?: VatRate;
    /** "2027-01-01" */
    nextVatFrom?: string;
    /** Ordningen i butiken, lägst först. */
    sortOrder?: number;
}

export interface VatLine {
    rate: number;
    /** Summa inklusive moms, varor och fördelad frakt. */
    gross: number;
    /** Varav frakt. */
    shipping: number;
    vat: number;
    net: number;
}

export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** Dagens datum i Sverige, "2026-10-02". */
export const stockholmDate = (at: Date = new Date()): string =>
    new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);

/** Satsen som gäller vid en tidpunkt, med en eventuell planerad ändring. */
export const effectiveVatRate = (category: Pick<ProductCategory, 'vatRate' | 'nextVatRate' | 'nextVatFrom'>, at: Date = new Date()): VatRate =>
    category.nextVatRate !== undefined && category.nextVatFrom && stockholmDate(at) >= category.nextVatFrom
        ? category.nextVatRate
        : category.vatRate;

/** Momsen som ingår i ett belopp inklusive moms. */
export const vatIncluded = (gross: number, rate: number): number => {
    // Symmetrisk avrundning, så att en avräkning (negativt belopp) blir exakt spegelbilden.
    // Räknat i hela ören så att exakt ett halvt öre avrundas uppåt, som round() i databasen.
    const v = Math.round((Math.round(Math.abs(gross) * 100) * rate) / (100 + rate)) / 100;
    return gross < 0 ? -v : v;
};

/**
 * Moms per sats för en order: varorna per sats plus fördelad frakt.
 * Sorterad efter sats, lägst först.
 */
/** Högsta livsmedelsmomsen (6 % nu, 12 % från 2028). Över den: 25 %-varor. */
const FOOD_RATE_MAX = 12;

export const vatBreakdown = (lines: { rate: number; amount: number }[], shipping = 0): VatLine[] => {
    const goods = new Map<number, number>();
    for (const l of lines) goods.set(l.rate, round2((goods.get(l.rate) || 0) + l.amount));
    // Fraktens moms (revisorns regel, som order_vat_breakdown): finns en vara
    // med 25 % moms får hela frakten 25 %. Annars (bara livsmedel) fördelas den
    // efter värdet exkl. moms: alla utom den största gruppen avrundas, den
    // största tar resten.
    const net = new Map([...goods.entries()].map(([rate, value]) => [rate, (value * 100) / (100 + rate)]));
    const totalNet = [...net.values()].reduce((s, v) => s + v, 0);
    const ship = new Map<number, number>();
    const topRate = Math.max(...goods.keys());
    if (shipping > 0 && totalNet > 0 && topRate > FOOD_RATE_MAX) {
        ship.set(topRate, round2(shipping));
    } else if (shipping > 0 && totalNet > 0) {
        const byValue = [...net.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
        let rest = round2(shipping);
        for (const [rate, value] of byValue.slice(1)) {
            const share = round2((shipping * value) / totalNet);
            ship.set(rate, share);
            rest = round2(rest - share);
        }
        ship.set(byValue[0][0], rest);
    }

    return [...goods.entries()]
        .map(([rate, value]) => {
            const shipping = ship.get(rate) || 0;
            const gross = round2(value + shipping);
            const vat = vatIncluded(gross, rate);
            return { rate, gross, shipping, vat, net: round2(gross - vat) };
        })
        .sort((a, b) => a.rate - b.rate);
};

/** Summerar flera uppställningar (flera ordrar, eller en faktura) per sats. */
export const sumVatLines = (groups: VatLine[][]): VatLine[] => {
    const map = new Map<number, VatLine>();
    for (const lines of groups) {
        for (const l of lines) {
            const cur = map.get(l.rate) || { rate: l.rate, gross: 0, shipping: 0, vat: 0, net: 0 };
            map.set(l.rate, {
                rate: l.rate,
                gross: round2(cur.gross + l.gross),
                shipping: round2(cur.shipping + l.shipping),
                vat: round2(cur.vat + l.vat),
                net: round2(cur.net + l.net),
            });
        }
    }
    return [...map.values()].sort((a, b) => a.rate - b.rate);
};

/**
 * Fördelar ett avdrag (t.ex. gruppens vinst) på satserna efter värdet, så
 * att delarna går jämnt upp. Returnerar avdraget per sats.
 */
export const splitByValue = (amount: number, valueByRate: Map<number, number>): Map<number, number> => {
    const total = [...valueByRate.values()].reduce((s, v) => s + v, 0);
    const out = new Map<number, number>();
    if (total <= 0 || valueByRate.size === 0) return out;
    const byValue = [...valueByRate.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
    let rest = round2(amount);
    for (const [rate, value] of byValue.slice(1)) {
        const share = round2((amount * value) / total);
        out.set(rate, share);
        rest = round2(rest - share);
    }
    out.set(byValue[0][0], rest);
    return out;
};

/** "12 %" */
export const formatVatRate = (rate: number): string => `${rate} %`;

/** Belopp med två decimaler, "1 078,02". */
export const formatOre = (n: number): string =>
    n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
